import { Container } from '@/components/ui/Container'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/utils'
import type { SalesOrderHead, SalesOrderRow } from '@/types/salesOrder'

const PANEL_LABEL = 'text-[10px] font-semibold uppercase tracking-wide text-gray-500'

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 2 }).format(value)
}

/** A date the ERP did not state reads as a dash, not as an empty cell. */
function orDash(value: string | null | undefined): string {
  return value && value.trim() !== '' ? value : '—'
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('px-4 py-3', className)}>
      <p className={PANEL_LABEL}>{label}</p>
      <div className="mt-0.5 text-sm text-gray-700">{children}</div>
    </div>
  )
}

function addressLines(head: SalesOrderHead): string[] {
  const street = [head.delivery_address_street, head.delivery_address_house_number]
    .filter((part) => part && part.trim() !== '')
    .join(' ')
  const city = [head.delivery_address_postal_code, head.delivery_address_city]
    .filter((part) => part && part.trim() !== '')
    .join(' ')

  return [
    head.delivery_address_company_name,
    head.delivery_address_care_of ? `c/o ${head.delivery_address_care_of}` : '',
    street,
    city,
    head.delivery_address_country,
    head.delivery_address_additional_information,
  ].filter((line) => line && line.trim() !== '')
}

/**
 * The ERP's own head for the order it raised.
 *
 * Nothing here is editable and nothing offers to be. The order exists in the ERP, so this is a
 * record of what it says rather than a form that could change it, and the absence of any affordance
 * is the honest way to say so.
 */
export function SalesOrderHeadPanel({ head }: { head: SalesOrderHead }) {
  const lines = addressLines(head)

  return (
    <Container className="min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
        <div className="min-w-0">
          <p className={PANEL_LABEL}>Sales order</p>
          <p className="mt-0.5 text-base font-semibold text-gray-900">{orDash(head.order_number)}</p>
        </div>
        {head.status && <Badge tone="success">{head.status}</Badge>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2">
        <Field label="Customer">
          <span className="block">{orDash(head.customer_number)}</span>
          <span className="block text-gray-500">{orDash(head.customer_name)}</span>
        </Field>
        <Field label="Customer order number">{orDash(head.customer_order_number)}</Field>

        <Field label="Delivery address">
          {lines.length === 0 ? (
            <span className="text-gray-400">No delivery address</span>
          ) : (
            <address className="block not-italic leading-snug">
              {lines.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </address>
          )}
        </Field>
        <Field label="Contact person">
          <span className="block">{orDash(head.customer_contact_person)}</span>
          {head.supplier_contact_person && (
            <span className="block text-gray-500">Ours: {head.supplier_contact_person}</span>
          )}
        </Field>

        <Field label="Delivery date">{orDash(head.delivery_date)}</Field>
        <Field label="Created">{orDash(head.creation_date)}</Field>

        <Field label="Payment terms">{orDash(head.payment_terms)}</Field>
        <Field label="Delivery terms">{orDash(head.delivery_terms)}</Field>

        <Field label="Delivery method">{orDash(head.delivery_method)}</Field>
        <Field label="Goods label">{orDash(head.goods_label)}</Field>
      </div>

      {(head.external_comment || head.internal_comment) && (
        <div className="grid grid-cols-1 border-t border-gray-100 sm:grid-cols-2">
          {head.external_comment && <Field label="Comment">{head.external_comment}</Field>}
          {head.internal_comment && <Field label="Internal comment">{head.internal_comment}</Field>}
        </div>
      )}
    </Container>
  )
}

const CELL = 'px-3 py-2 align-top text-sm text-gray-700'

/**
 * The lines the ERP accepted.
 *
 * No match state and no stock: the ERP has resolved the articles already, so the columns the
 * customer order needs to show its working have nothing left to say here.
 */
export function SalesOrderRowsPanel({ rows }: { rows: SalesOrderRow[] }) {
  if (rows.length === 0) {
    return (
      <Container className="min-w-0">
        <p className="px-4 py-6 text-sm text-gray-400">This sales order has no rows.</p>
      </Container>
    )
  }

  return (
    <Container className="min-w-0 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/60">
              <th className={cn(CELL, PANEL_LABEL, 'w-12 text-left')}>Pos</th>
              <th className={cn(CELL, PANEL_LABEL, 'text-left')}>Article</th>
              <th className={cn(CELL, PANEL_LABEL, 'text-right')}>Quantity</th>
              <th className={cn(CELL, PANEL_LABEL, 'text-right')}>Unit price</th>
              <th className={cn(CELL, PANEL_LABEL, 'text-left')}>Delivery</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-gray-50 last:border-0">
                <td className={cn(CELL, 'tabular-nums text-gray-400')}>{orDash(row.position)}</td>
                <td className={CELL}>
                  <span className="block font-medium">{orDash(row.article_number)}</span>
                  <span className="block text-gray-500">{orDash(row.article_description)}</span>
                  {row.customer_article_number && (
                    <span className="block text-xs text-gray-400">
                      Their number: {row.customer_article_number}
                    </span>
                  )}
                </td>
                <td className={cn(CELL, 'text-right tabular-nums')}>{formatNumber(row.quantity)}</td>
                <td className={cn(CELL, 'text-right tabular-nums')}>{formatNumber(row.unit_price)}</td>
                <td className={cn(CELL, 'whitespace-nowrap')}>{orDash(row.delivery_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Container>
  )
}
